import { makeAutoObservable } from 'mobx';
import type RootStore from '../RootStore';

interface FilePickerAcceptType {
    description: string;
    mimeTypes: string[];
}

interface SaveFilePickerOptions {
    suggestedName?: string;
    types?: FilePickerAcceptType[];
}

interface OpenFilePickerOptions {
    types?: FilePickerAcceptType[];
    excludeAcceptAllOption?: boolean;
    multiple?: boolean;
}

export class FileSystemStore {
    rootStore: RootStore;
    isSupported = 'showOpenFilePicker' in window;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false,
        });
        this.rootStore = rootStore;
    }

    async openFile(options?: OpenFilePickerOptions): Promise<File | null> {
        if (!this.isSupported) {
            throw new Error('File System API not supported');
        }

        try {
            const [fileHandle] = await (window as any).showOpenFilePicker(options);
            return await fileHandle.getFile();
        } catch (error: any) {
            if (error.name === 'AbortError') {
                return null;
            }
            throw error;
        }
    }

    async openFiles(options?: OpenFilePickerOptions): Promise<File[]> {
        if (!this.isSupported) {
            throw new Error('File System API not supported');
        }

        try {
            const handles = await (window as any).showOpenFilePicker({
                ...options,
                multiple: true,
            });

            const files = await Promise.all(
                handles.map((handle: any) => handle.getFile())
            );

            return files;
        } catch (error: any) {
            if (error.name === 'AbortError') {
                return [];
            }
            throw error;
        }
    }

    async saveFile(
        suggestedName: string,
        content: Blob | string,
        options?: SaveFilePickerOptions
    ): Promise<void> {
        if (!this.isSupported) {
            // Fallback to download
            const blob = typeof content === 'string'
                ? new Blob([content], { type: 'text/plain' })
                : content;

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = suggestedName;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        try {
            const handle = await (window as any).showSaveFilePicker({
                suggestedName,
                ...options,
            });

            const writable = await handle.createWritable();
            await writable.write(content);
            await writable.close();
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                throw error;
            }
        }
    }

    async openDirectory(): Promise<FileSystemDirectoryHandle | null> {
        if (!this.isSupported) {
            throw new Error('File System API not supported');
        }

        try {
            return await (window as any).showDirectoryPicker();
        } catch (error: any) {
            if (error.name === 'AbortError') {
                return null;
            }
            throw error;
        }
    }
}